import { ChangeDetectorRef, Pipe, PipeTransform } from '@angular/core';
import { CurrencyService, CurrencyFormattingConfig } from '../../core/services/currency.service';
import { Subscription } from 'rxjs';

/**
 * Usage examples:
 *  {{ amount | appCurrency }} -> uses currently selected currency
 *  {{ amount | appCurrency:'EUR' }} -> force EUR
 *  {{ amount | appCurrency:{ code: 'MAD' } }} -> force MAD config merge
 *  {{ priceObj | appCurrency }} where priceObj = { amount: 123.45 }
 */
@Pipe({
  name: 'appCurrency',
  pure: false // respond to runtime currency change
})
export class AppCurrencyPipe implements PipeTransform {
  private lastValue?: any;
  private lastResult = '';
  private sub: Subscription;
  private version = 0; // bump when currency changes

  constructor(private currencyService: CurrencyService, private cdr: ChangeDetectorRef) {
    this.sub = this.currencyService.currencyChanges.subscribe(() => {
      this.version++;
      this.cdr.markForCheck();
    });
  }

  transform(value: any, currencyOverride?: string | Partial<CurrencyFormattingConfig>): string {
    if (value == null || value === '') return '';

    let numeric: number | undefined;
    if (typeof value === 'number') numeric = value;
    else if (typeof value === 'string') numeric = Number(value);
    else if (typeof value === 'object') {
      // Support common shapes: { amount }, { sale_price: { amount } }, { sale_price }
      if ('amount' in value && typeof value.amount !== 'object') {
        numeric = Number(value.amount);
      } else if ('sale_price' in value) {
        const sp = (value as any).sale_price;
        if (typeof sp === 'number') numeric = sp; else if (sp && typeof sp.amount !== 'undefined') numeric = Number(sp.amount);
      }
    }

    if (numeric == null || isNaN(numeric)) return '';

    let override: Partial<CurrencyFormattingConfig> | undefined;
    if (currencyOverride) {
      if (typeof currencyOverride === 'string') {
        override = { code: currencyOverride };
      } else {
        override = currencyOverride;
      }
    }

    // If override specifies code, we try to derive defaults for that code by temporarily switching logic.
    let mergedOverride: Partial<CurrencyFormattingConfig> | undefined = undefined;
    if (override?.code && override.code !== this.currencyService.current.code) {
      // We do not change the global currency, just attempt to use mapping if exists
      const mapCandidate = (this as any).currencyService['currencyMap']?.[override.code];
      if (mapCandidate) {
        mergedOverride = { ...mapCandidate, ...override };
      } else {
        mergedOverride = override;
      }
    } else {
      mergedOverride = override;
    }

    const result = this.currencyService.formatRaw(numeric, mergedOverride);

    // Simple memoization
    if (this.lastValue === value && this.lastResult === result) {
      return this.lastResult;
    }

    this.lastValue = value;
    this.lastResult = result;
    return result;
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
